/*
 * Copyright 2026 North Carolina State University
 */

import React from 'react';
import {
  Box,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Typography,
  MenuItem
} from '@mui/material';
import { DAY_SHORT, DAY_LABELS, normalizeWeek } from '../../utils/waterSchedule';

/**
 * The editor for ONE irrigation schedule: a two-week day grid, how many times
 * a day, how long for, and notes.
 *
 * Deliberately shared between the client's request form and the admin's
 * schedule pane. The two must stay identical — an admin recording what was
 * asked for should be filling in the same boxes the client ticked, or the
 * schedule that ends up on the project will not match the request.
 *
 * Two weeks rather than one: alternate-day watering cannot be expressed in a
 * single week, and week 2 is what tells you whether "Mon, Wed, Fri" repeats or
 * shifts.
 */

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const SIXTY = Array.from({ length: 60 }, (_, i) => i);

const WeekGrid = ({ week1, week2, onWeekChange, disabled }) => {
  const rows = [
    { key: 'week1', label: 'Week 1', days: normalizeWeek(week1) },
    { key: 'week2', label: 'Week 2', days: normalizeWeek(week2) }
  ];

  const toggle = (weekKey, dayIndex, days) => {
    const next = [...days];
    next[dayIndex] = !next[dayIndex];
    onWeekChange(weekKey, next);
  };

  return (
    // `tableLayout: fixed` is what makes the seven days share the full width
    // evenly instead of the table collapsing to the width of its checkboxes.
    <Table
      sx={{
        width: '100%',
        tableLayout: 'fixed',
        '& td, & th': { px: 0.5, textAlign: 'center', borderBottom: 'none' }
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: 84, textAlign: 'left !important', pb: 0.5 }} />
          {DAY_SHORT.map((d, i) => (
            <TableCell key={d} sx={{ fontWeight: 600, fontSize: '0.85rem', pb: 0.5 }}>
              <abbr title={DAY_LABELS[i]} style={{ textDecoration: 'none' }}>{d}</abbr>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.key} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem', textAlign: 'left !important' }}>
              {row.label}
            </TableCell>
            {row.days.map((on, i) => (
              <TableCell key={`${row.key}-${i}`} sx={{ p: 0 }}>
                <Checkbox
                  checked={on}
                  disabled={disabled}
                  onChange={() => toggle(row.key, i, row.days)}
                  inputProps={{ 'aria-label': `${row.label} ${DAY_LABELS[i]}` }}
                />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

/**
 * @param {Object}   entry     the schedule being edited
 * @param {Function} onChange  (patch) => void, merged into the entry
 * @param {boolean}  disabled
 * @param {boolean}  readOnly  render values without inputs
 */
const WaterScheduleFields = ({ entry, onChange, disabled = false, readOnly = false }) => {
  const duration = entry?.duration || {};

  const patch = (changes) => onChange({ ...entry, ...changes });

  if (readOnly) {
    return (
      <Box>
        <WeekGrid week1={entry?.week1} week2={entry?.week2} onWeekChange={() => {}} disabled />
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Times per day</Typography>
            <Typography variant="body2">{entry?.times_per_day || '—'}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Duration</Typography>
            <Typography variant="body2">
              {[duration.hours ? `${duration.hours} h` : null,
                duration.minutes ? `${duration.minutes} min` : null,
                duration.seconds ? `${duration.seconds} sec` : null]
                .filter(Boolean).join(' ') || '—'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Comments</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{entry?.comments || '—'}</Typography>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Watering days
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Tick the days you want to water. Week 2 is there so an alternate-day pattern is clear.
      </Typography>
      <WeekGrid
        week1={entry?.week1}
        week2={entry?.week2}
        disabled={disabled}
        onWeekChange={(weekKey, days) => patch({ [weekKey]: days })}
      />

      {/* Full-size inputs on their own rows. Squeezed into thirds at
          size="small" the labels — "How many times a day?" in particular —
          were clipped to the point of being unreadable. */}
      <Grid container spacing={2.5} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={5}>
          <TextField
            fullWidth
            type="number"
            label="How many times a day?"
            value={entry?.times_per_day ?? ''}
            onChange={(e) => patch({ times_per_day: e.target.value })}
            disabled={disabled}
            inputProps={{ min: 0, max: 96 }}
          />
        </Grid>

        {/* Hours / minutes / seconds, so both "15 seconds" and
            "2 hours 38 minutes" are expressible. */}
        <Grid item xs={12} sm={7}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Duration
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <TextField
              select fullWidth label="Hours"
              value={entry?.duration?.hours ?? ''}
              onChange={(e) => patch({ duration: { ...duration, hours: e.target.value } })}
              disabled={disabled}
            >
              <MenuItem value="">—</MenuItem>
              {HOURS.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
            </TextField>
            <TextField
              select fullWidth label="Minutes"
              value={entry?.duration?.minutes ?? ''}
              onChange={(e) => patch({ duration: { ...duration, minutes: e.target.value } })}
              disabled={disabled}
            >
              <MenuItem value="">—</MenuItem>
              {SIXTY.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
            <TextField
              select fullWidth label="Seconds"
              value={entry?.duration?.seconds ?? ''}
              onChange={(e) => patch({ duration: { ...duration, seconds: e.target.value } })}
              disabled={disabled}
            >
              <MenuItem value="">—</MenuItem>
              {SIXTY.map(sec => <MenuItem key={sec} value={sec}>{sec}</MenuItem>)}
            </TextField>
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Comments"
            value={entry?.comments ?? ''}
            onChange={(e) => patch({ comments: e.target.value })}
            disabled={disabled}
            placeholder="Anything specific about this water source"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default WaterScheduleFields;
